import React from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import LoadingButton from "@mui/lab/LoadingButton";
import { toast } from "react-toastify";

import { postRightworkAPI } from "../requests/utilities/requests";
import {
  API_post_certificate_type,
  API_archive_certificate_type,
  API_set_certificate_type_overview_visibility,
  API_set_certificate_type_role_requirements,
} from "../../../constants";

const modalBoxSx = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  // Wider than the usual modal on purpose: the "Required for" row renders every
  // role that requires a certificate, and at 560px a company with a dozen roles
  // wrapped to three or four lines per row.
  maxWidth: "720px",
  bgcolor: "background.paper",
  borderRadius: "16px",
  boxShadow: 24,
  outline: "none",
};

const checkboxSx = {
  color: "#6d28d9",
  "&.Mui-checked": { color: "#6d28d9" },
  "&.MuiCheckbox-indeterminate": { color: "#6d28d9" },
};

const textFieldSx = {
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#6d28d9",
  },
};

function ManageCertificateTypesModal({
  open,
  onClose,
  homeStore,
  certificateTypes,
  systemTypes: systemTypesProp,
  requiredSlugs,
  roleNames,
  onChanged,
  onVisibilityChanged,
  onRequirementsChanged,
}) {
  const requiredSlugSet = React.useMemo(
    () => new Set(requiredSlugs || []),
    [requiredSlugs]
  );

  // Company roles (id + name) and, per slug, which role ids currently require it —
  // derived from roleNames (which already carries required_certificate_types), so the
  // editor here and the Role settings page edit one source of truth.
  const companyRoles = React.useMemo(
    () =>
      (roleNames || []).map((r) => ({ id: r.id, label: r.role_name || "Role" })),
    [roleNames]
  );
  const roleIdsBySlug = React.useMemo(() => {
    const map = {};
    (roleNames || []).forEach((r) => {
      (r.required_certificate_types || []).forEach((slug) => {
        (map[slug] = map[slug] || []).push(r.id);
      });
    });
    return map;
  }, [roleNames]);
  const [newLabel, setNewLabel] = React.useState("");
  const [newHasExpiration, setNewHasExpiration] = React.useState(true);
  const [savingNew, setSavingNew] = React.useState(false);
  const [busyValue, setBusyValue] = React.useState(null);
  const [editingUuid, setEditingUuid] = React.useState(null);
  const [editingLabel, setEditingLabel] = React.useState("");

  // Prefer the unfiltered built-in list (so flag-gated types like break_waiver stay
  // toggleable even in stores where their flag hides them from the catalog); fall back
  // to the catalog's system entries.
  const systemTypes =
    Array.isArray(systemTypesProp) && systemTypesProp.length > 0
      ? systemTypesProp
      : (certificateTypes || []).filter((t) => t.is_system);
  const customTypes = (certificateTypes || []).filter((t) => !t.is_system);

  const handleCreate = async () => {
    const label = newLabel.trim();
    if (!label) {
      toast.error("Please enter a name");
      return;
    }
    setSavingNew(true);
    try {
      await postRightworkAPI(API_post_certificate_type, {
        store_id: homeStore,
        label,
        has_expiration: newHasExpiration,
      });
      toast.success("Certificate type added");
      setNewLabel("");
      setNewHasExpiration(true);
      if (onChanged) await onChanged();
    } catch (err) {
      toast.error(
        err?.response?.data?.system?.message || "Failed to add certificate type"
      );
    } finally {
      setSavingNew(false);
    }
  };

  const startRename = (type) => {
    setEditingUuid(type.uuid);
    setEditingLabel(type.label);
  };

  const cancelRename = () => {
    setEditingUuid(null);
    setEditingLabel("");
  };

  const handleRename = async (type) => {
    const label = editingLabel.trim();
    if (!label) {
      toast.error("Please enter a name");
      return;
    }
    if (label === type.label) {
      cancelRename();
      return;
    }
    setBusyValue(type.value);
    try {
      await postRightworkAPI(API_post_certificate_type, {
        store_id: homeStore,
        certificate_type_uuid: type.uuid,
        label,
        has_expiration: type.has_expiration,
      });
      toast.success("Certificate type renamed");
      cancelRename();
      if (onChanged) await onChanged();
    } catch (err) {
      toast.error(
        err?.response?.data?.system?.message || "Failed to rename certificate type"
      );
    } finally {
      setBusyValue(null);
    }
  };

  const handleToggleOverviewVisibility = async (type) => {
    setBusyValue(type.value);
    try {
      await postRightworkAPI(API_set_certificate_type_overview_visibility, {
        store_id: homeStore,
        slug: type.value,
        hidden: !type.overview_hidden,
      });
      // Visibility only changes the hidden-slug list — no need to reload certificates
      // or role/employee data (which shows the full-table spinner). A light catalog
      // refetch updates the hidden set and re-filters the overview in place.
      const refresh = onVisibilityChanged || onChanged;
      if (refresh) await refresh();
    } catch (err) {
      toast.error(
        err?.response?.data?.system?.message || "Failed to update visibility"
      );
    } finally {
      setBusyValue(null);
    }
  };

  // A type can carry role requirements only if it's requirable: uploadable, not the
  // generic "Other"/Emergency Contact, and not hidden (hide and require are exclusive).
  const isRequirable = (type) =>
    type.uploadable &&
    type.value !== "other" &&
    type.value !== "emergency_contact" &&
    !type.overview_hidden;

  const handleSetRoleRequirements = async (type, roleIds) => {
    setBusyValue(type.value);
    try {
      await postRightworkAPI(API_set_certificate_type_role_requirements, {
        store_id: homeStore,
        slug: type.value,
        role_ids: roleIds,
      });
      // Refresh the catalog + roles (updates this editor and the overview's missing
      // rows) without the full-table spinner; the inline spinner below covers this.
      const refresh = onRequirementsChanged || onChanged;
      if (refresh) await refresh();
    } catch (err) {
      toast.error(
        err?.response?.data?.system?.message ||
          "Failed to update role requirements"
      );
    } finally {
      setBusyValue(null);
    }
  };

  // Inline "Required for roles" editor, shown under each requirable type. Writes back
  // to role.required_certificate_types (same data the Role settings page edits).
  const renderRoleRequirements = (type) => {
    if (!isRequirable(type) || companyRoles.length === 0) return null;
    const selected = roleIdsBySlug[type.value] || [];
    const saving = busyValue === type.value;
    const allRoleIds = companyRoles.map((r) => r.id);
    const allSelected = selected.length === allRoleIds.length;
    const someSelected = selected.length > 0 && !allSelected;

    // The "All roles" row emits a sentinel; intercept it to select all / clear.
    const handleChange = (e) => {
      const vals = e.target.value;
      if (vals.includes("__all__")) {
        handleSetRoleRequirements(type, allSelected ? [] : allRoleIds);
        return;
      }
      handleSetRoleRequirements(type, vals);
    };

    // items-start, not center: the select grows to several lines when many roles
    // are selected, and a vertically-centred label reads oddly against it. The
    // label's pt aligns it with the select's first line.
    return (
      <div className="mt-2 flex items-start gap-2">
        <span className="text-xs text-slate-500 shrink-0 pt-1.5">
          Required for
        </span>
        <Select
          multiple
          size="small"
          displayEmpty
          value={selected}
          disabled={saving}
          onChange={handleChange}
          renderValue={(vals) =>
            vals.length === 0
              ? "No roles"
              : allSelected
              ? "All roles"
              : companyRoles
                  .filter((r) => vals.includes(r.id))
                  .map((r) => r.label)
                  .join(", ")
          }
          sx={{
            flex: 1,
            // Flex items default to min-width:auto, so without this the control
            // grows to fit the joined role list and runs off the modal.
            minWidth: 0,
            fontSize: 13,
            backgroundColor: "white",
            "& .MuiSelect-select": {
              paddingY: "6px",
              // Wrap rather than truncate. This row exists to show WHICH roles
              // require the certificate, so ellipsising it hides the one thing
              // it is for and forces the dropdown open to read the list.
              whiteSpace: "normal",
              overflowWrap: "anywhere",
              lineHeight: 1.4,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#6d28d9",
            },
          }}
        >
          <MenuItem value="__all__">
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected}
              sx={checkboxSx}
              size="small"
            />
            <ListItemText
              primary="All roles"
              primaryTypographyProps={{ style: { fontWeight: "bold" } }}
            />
          </MenuItem>
          {companyRoles.map((r) => (
            <MenuItem key={r.id} value={r.id}>
              <Checkbox checked={selected.includes(r.id)} sx={checkboxSx} size="small" />
              <ListItemText primary={r.label} />
            </MenuItem>
          ))}
        </Select>
        {saving && (
          <CircularProgress size={16} sx={{ color: "#6d28d9", flexShrink: 0 }} />
        )}
      </div>
    );
  };

  const handleToggleExpiration = async (type) => {
    setBusyValue(type.value);
    try {
      await postRightworkAPI(API_post_certificate_type, {
        store_id: homeStore,
        certificate_type_uuid: type.uuid,
        label: type.label,
        has_expiration: !type.has_expiration,
      });
      if (onChanged) await onChanged();
    } catch (err) {
      toast.error(
        err?.response?.data?.system?.message || "Failed to update certificate type"
      );
    } finally {
      setBusyValue(null);
    }
  };

  const handleArchive = async (type) => {
    if (
      !window.confirm(
        `Remove "${type.label}"? Existing certificates of this type are kept, but it will no longer be selectable.`
      )
    ) {
      return;
    }
    setBusyValue(type.value);
    try {
      await postRightworkAPI(API_archive_certificate_type, {
        store_id: homeStore,
        certificate_type_uuid: type.uuid,
        slug: type.value,
      });
      toast.success("Certificate type removed");
      if (onChanged) await onChanged();
    } catch (err) {
      toast.error(
        err?.response?.data?.system?.message || "Failed to remove certificate type"
      );
    } finally {
      setBusyValue(null);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalBoxSx}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <p className="text-lg font-semibold text-slate-900">
            Manage Certificate Types
          </p>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </div>

        <div className="px-6 py-5 max-h-[80vh] overflow-auto">
          {/* Add new */}
          <p className="text-sm font-medium text-slate-700 mb-2">
            Add a certificate type
          </p>
          <div className="flex items-start gap-2">
            <TextField
              fullWidth
              size="small"
              placeholder="e.g. ServSafe Certification"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleCreate();
              }}
              sx={textFieldSx}
            />
            <LoadingButton
              variant="contained"
              loading={savingNew}
              onClick={handleCreate}
              startIcon={<AddIcon />}
              sx={{
                textTransform: "none",
                borderRadius: "10px",
                paddingX: "16px",
                paddingY: "8px",
                whiteSpace: "nowrap",
                backgroundColor: "#6d28d9",
                "&:hover": { backgroundColor: "#5b21b6" },
              }}
            >
              Add
            </LoadingButton>
          </div>
          <label className="flex items-center mt-1 -ml-2 cursor-pointer select-none">
            <Checkbox
              checked={newHasExpiration}
              onChange={(e) => setNewHasExpiration(e.target.checked)}
              sx={checkboxSx}
              size="small"
            />
            <span className="text-sm text-slate-600">Tracks an expiration date</span>
          </label>

          {/* Custom types */}
          <p className="text-sm font-medium text-slate-700 mt-6 mb-2">
            Your certificate types
          </p>
          {customTypes.length === 0 ? (
            <p className="text-sm text-slate-400 mb-2">
              No custom types yet. Add one above.
            </p>
          ) : (
            <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg">
              {customTypes.map((type) => (
                <div key={type.value} className="px-3 py-2.5">
                  <div className="flex items-center justify-between gap-2">
                  {editingUuid === type.uuid ? (
                    <>
                      <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        value={editingLabel}
                        onChange={(e) => setEditingLabel(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleRename(type);
                          if (e.key === "Escape") cancelRename();
                        }}
                        sx={textFieldSx}
                      />
                      <div className="flex items-center gap-1 shrink-0">
                        <Tooltip title="Save">
                          <IconButton
                            size="small"
                            disabled={busyValue === type.value}
                            onClick={() => handleRename(type)}
                          >
                            <CheckIcon fontSize="small" sx={{ color: "#6d28d9" }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <IconButton size="small" onClick={cancelRename}>
                            <CloseIcon fontSize="small" sx={{ color: "#64748b" }} />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </>
                  ) : (
                    <>
                      <span className="text-sm text-slate-900 truncate">
                        {type.label}
                      </span>
                      <div className="flex items-center gap-2 shrink-0">
                        <Tooltip title="Tracks an expiration date">
                          <label className="flex items-center cursor-pointer select-none">
                            <Checkbox
                              checked={type.has_expiration}
                              disabled={busyValue === type.value}
                              onChange={() => handleToggleExpiration(type)}
                              sx={checkboxSx}
                              size="small"
                            />
                            <span className="text-xs text-slate-500">Expires</span>
                          </label>
                        </Tooltip>
                        <Tooltip title="Rename">
                          <IconButton
                            size="small"
                            disabled={busyValue === type.value}
                            onClick={() => startRename(type)}
                          >
                            <EditOutlinedIcon
                              fontSize="small"
                              sx={{ color: "#64748b" }}
                            />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove">
                          <IconButton
                            size="small"
                            disabled={busyValue === type.value}
                            onClick={() => handleArchive(type)}
                          >
                            <DeleteOutlineIcon
                              fontSize="small"
                              sx={{ color: "#ef4444" }}
                            />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </>
                  )}
                  </div>
                  {editingUuid !== type.uuid && renderRoleRequirements(type)}
                </div>
              ))}
            </div>
          )}

          {/* System types: can't be renamed/removed, but can be hidden from the
              Certificates overview. */}
          <p className="text-sm font-medium text-slate-700 mt-6 mb-2">
            Built-in types
          </p>
          <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg bg-slate-50">
            {systemTypes.map((type) => {
              // A required type can't be hidden (hide and require are mutually
              // exclusive). Un-hiding an already-hidden type is always allowed.
              const blockedByRequirement =
                !type.overview_hidden && requiredSlugSet.has(type.value);
              return (
                <div key={type.value} className="px-3 py-2.5">
                  <div className="flex items-center justify-between">
                    <span
                      className={`text-sm ${
                        type.overview_hidden ? "text-slate-400 italic" : "text-slate-700"
                      }`}
                    >
                      {type.label}
                      {blockedByRequirement && (
                        <span className="text-xs text-slate-400 ml-2">
                          (required by a role)
                        </span>
                      )}
                    </span>
                    <Tooltip
                      title={
                        blockedByRequirement
                          ? "Required by a role - remove the requirement below to hide it"
                          : type.overview_hidden
                          ? "Hidden from the Certificates overview - click to show"
                          : "Showing in the Certificates overview - click to hide"
                      }
                    >
                      {/* span wrapper so the tooltip still shows on a disabled button */}
                      <span>
                        <IconButton
                          size="small"
                          disabled={busyValue === type.value || blockedByRequirement}
                          onClick={() => handleToggleOverviewVisibility(type)}
                        >
                          {type.overview_hidden ? (
                            <VisibilityOffIcon fontSize="small" sx={{ color: "#94a3b8" }} />
                          ) : (
                            <VisibilityIcon fontSize="small" sx={{ color: "#6d28d9" }} />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </div>
                  {renderRoleRequirements(type)}
                </div>
              );
            })}
          </div>
          <p className="text-xs text-slate-400 mt-2">
            Built-in types can't be renamed or removed, but you can hide any you don't
            use. Hidden types are removed from the Certificates list, filters, and the
            add-certificate type picker everywhere. Certificates already on file stay
            on employee profiles, and Meal Period Waiver / Emergency Contact behavior is
            unchanged.
          </p>
        </div>
      </Box>
    </Modal>
  );
}

export default ManageCertificateTypesModal;
